import { styled } from '@linaria/react';
import type { Dayjs } from 'dayjs';
import type { ReactElement } from 'react';

interface TimelineGridProps {
  timelineStart: Dayjs;
  timelineEnd: Dayjs;
  today: Dayjs;
  timelineWidth: string | number;
  totalDays: number;
  showQuarters?: boolean;
  showTodayLine?: boolean;
}

export function TimelineGrid({
  timelineStart,
  timelineEnd,
  today,
  timelineWidth,
  totalDays,
  showQuarters = false,
  showTodayLine = true,
}: TimelineGridProps) {
  const renderMonthGrid = () => {
    const months: ReactElement[] = [];
    let currentMonth = timelineStart.startOf('month');
    const endMonth = timelineEnd.endOf('month');

    while (currentMonth.isBefore(endMonth) || currentMonth.isSame(endMonth, 'month')) {
      const daysFromStart = currentMonth.diff(timelineStart, 'day');
      const leftPercent = totalDays === 0 ? 0 : (daysFromStart / totalDays) * 100;
      months.push(
        <div
          key={currentMonth.format('YYYY-MM')}
          style={{
            position: 'absolute',
            left: `${leftPercent}%`,
            top: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <MonthLine />
          <MonthLabel>{currentMonth.format('MMM YYYY')}</MonthLabel>
        </div>,
      );
      currentMonth = currentMonth.add(1, 'month');
    }

    return months;
  };

  const renderQuarterGrid = () => {
    const quarters: ReactElement[] = [];
    let currentDate = timelineStart.startOf('year');
    const endDate = timelineEnd.endOf('year');

    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate)) {
      const quarter = Math.floor(currentDate.month() / 3) + 1;
      const year = currentDate.year();

      const daysFromStart = currentDate.diff(timelineStart, 'day');
      const leftPercent = totalDays === 0 ? 0 : (daysFromStart / totalDays) * 100;

      quarters.push(
        <div
          key={`${year}-Q${quarter}`}
          style={{
            position: 'absolute',
            left: `${leftPercent}%`,
            top: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <MonthLine />
          <VerticalMonthLabel>{`Q${quarter} ${year}`}</VerticalMonthLabel>
        </div>,
      );

      currentDate = currentDate.add(3, 'month');
    }

    return quarters;
  };

  const todayDaysFromStart = today.diff(timelineStart, 'day');
  const todayLeftPercent = totalDays === 0 ? 0 : (todayDaysFromStart / totalDays) * 100;

  return (
    <TimelineGridWrapper style={{ width: timelineWidth }}>
      {showQuarters ? renderQuarterGrid() : renderMonthGrid()}
      {showTodayLine && <TodayLine style={{ left: `${todayLeftPercent}%` }} />}
    </TimelineGridWrapper>
  );
}

// Styled components
const TimelineGridWrapper = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
`;

const MonthLine = styled.div`
  position: absolute;
  left: 0;
  width: 1px;
  height: 100%;
  background: var(--sky-base);
  opacity: 0.25;
  z-index: 10;
  pointer-events: none;
`;

const MonthLabel = styled.div`
  position: absolute;
  top: 50%;
  left: 8px;
  transform: translateY(-50%);
  font-size: 11px;
  font-weight: 600;
  color: var(--ink-base);
  white-space: nowrap;
  background: var(--sky-lightest);
  padding: 2px 6px;
  border-radius: 3px;
`;

const VerticalMonthLabel = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%) rotate(-45deg);
  font-size: 11px;
  font-weight: 600;
  color: var(--ink-base);
  white-space: nowrap;
  background: var(--sky-lightest);
  padding: 2px 6px;
  border-radius: 3px;
`;

const TodayLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: var(--red-base);
  z-index: 15;
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 8px;
    height: 8px;
    background: var(--red-base);
    border-radius: 50%;
    border: 2px solid var(--base-white);
  }
`;
